// src/components/payroll/PayslipList.js — Phase 2
// Payslip List page for a completed payroll run.
// Phase 3: modal (selectedEmployee / modalOpen wired but modal component not yet added)
// Phase 4a: individual PDF download
// Phase 4c: bulk PDF download

import { useState, useEffect, useMemo } from 'react';
import Icon from "../icons/Icon";
import { useParams, useNavigate } from 'react-router-dom';
import { useCompanyValidation } from '../../hooks/useCompanyValidation';
import { useAuth } from '../../hooks/useAuth';
import { db } from '../../firebase/config';
import {
  doc, getDoc, getDocs, collection, query, where, limit,
} from 'firebase/firestore';
import { calculateEmployeeFinancials, calculatePayrollSummary } from '../../utils/payrollCalculations';
import { employeeDepartment, matchesEmployeeSearch } from '../../utils/employeeSearch';
import { loadRunEmployees } from '../../services/payrollRunStore';
import { useToast } from '../../contexts/ToastContext';
import PayslipModal from './PayslipModal';
import { loadPayslipPdf, loadBulkPayslipPdf, PDF_LOAD_ERROR } from '../../services/pdfLoader';
import '../../styles/sections/payslips.css';

const PayslipList = () => {
  const { slug, runId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { company, loading: companyLoading } = useCompanyValidation(slug);
  const toast = useToast();

  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [payslipSettings, setPayslipSettings] = useState({ layout: '3x2', format: 'bw', paper: 'a4' });

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  // Permission guard — redirect if missing payslip:view
  useEffect(() => {
    if (!companyLoading && !hasPermission('payslip:view')) {
      toast.error('You do not have permission to view payslips.');
      navigate(`/company/${slug}/payroll`, { replace: true });
    }
  }, [companyLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (company) {
      fetchData();
    }
  }, [company]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch payslip settings (non-blocking — use defaults on error)
      try {
        const settingsSnap = await getDoc(
          doc(db, 'companies', company.id, 'settings', 'payslips')
        );
        if (settingsSnap.exists()) {
          setPayslipSettings({ layout: '3x2', format: 'bw', paper: 'a4', ...settingsSnap.data() });
        }
      } catch (_) { /* keep defaults */ }

      // Fetch the payroll run.
      // runId in URL = run.runId (human-readable, e.g. "202603-017") or Firestore doc ID.
      // Try query by runId field first, fall back to direct doc fetch.
      let rawData = null;
      let docId = null;

      const q = query(
        collection(db, 'companies', company.id, 'payrollRuns'),
        where('runId', '==', runId),
        limit(1)
      );
      const qSnap = await getDocs(q);
      if (!qSnap.empty) {
        docId = qSnap.docs[0].id;
        rawData = { id: docId, ...qSnap.docs[0].data() };
      } else {
        const docSnap = await getDoc(
          doc(db, 'companies', company.id, 'payrollRuns', runId)
        );
        if (docSnap.exists()) {
          docId = docSnap.id;
          rawData = { id: docId, ...docSnap.data() };
        }
      }

      if (!rawData) {
        setError('Payroll run not found.');
        return;
      }

      if (rawData.status !== 'completed') {
        setError('Payslips are only available for completed payroll runs.');
        return;
      }

      // Hydrate per-employee lines (subcollection for converted runs, inline for
      // historical) — the parent no longer carries employees (C3).
      rawData.employees = await loadRunEmployees(company.id, docId, rawData);

      // Parse timestamps
      rawData.periodStart = rawData.periodStart?.toDate
        ? rawData.periodStart.toDate()
        : new Date(rawData.periodStart);
      rawData.periodEnd = rawData.periodEnd?.toDate
        ? rawData.periodEnd.toDate()
        : new Date(rawData.periodEnd);
      rawData.lastModified = rawData.lastModified?.toDate
        ? rawData.lastModified.toDate()
        : null;

      setRun(rawData);
    } catch (err) {
      console.error('[PayslipList] Error fetching data:', err);
      setError('Failed to load payslip data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Formatters ──────────────────────────────────────────────

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 2,
    })
      .format(amount || 0)
      .replace('PHP', '₱');

  const formatPeriod = (start, end) => {
    const opts = { month: 'short', day: 'numeric' };
    const startStr = new Intl.DateTimeFormat('en-US', opts).format(start);
    const endStr = new Intl.DateTimeFormat('en-US', opts).format(end);
    return `${startStr} – ${endStr}, ${end.getFullYear()}`;
  };

  const getSalaryTypeLabel = (emp) => {
    const type = emp.employment?.salaryType;
    if (type === 'daily') {
      const rate = Number(emp.employment?.dailySalary || 0).toLocaleString('en-PH');
      return `Daily · ₱${rate}/day`;
    }
    if (type === 'monthly') {
      const rate = Number(emp.employment?.monthlySalary || 0).toLocaleString('en-PH');
      return `Monthly · ₱${rate}/mo`;
    }
    return type || '—';
  };

  const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.split(' ');
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name.substring(0, 2).toUpperCase();
  };

  const getEmployeeName = (emp) =>
    emp.employeeName ||
    emp.personalInfo?.fullName ||
    `${emp.personalInfo?.firstName || ''} ${emp.personalInfo?.lastName || ''}`.trim() ||
    'Unknown';

  const getEmployeeFinancials = (emp) => {
    const result = calculateEmployeeFinancials(emp);
    return { gross: result.gross, deductions: result.totalDeductions, net: result.net };
  };

  // ── Derived state ────────────────────────────────────────────

  const filteredEmployees = useMemo(() => {
    if (!run?.employees) return [];
    // No department dropdown on this list, so department is searchable (C9).
    return run.employees.filter((emp) => matchesEmployeeSearch(emp, searchQuery, [employeeDepartment(emp)]));
  }, [run, searchQuery]);

  const summary = useMemo(
    () => (run?.employees ? calculatePayrollSummary(run.employees) : { totalGross: 0, totalNet: 0 }),
    [run]
  );

  const settingsHint = useMemo(() => {
    const layoutLabel = payslipSettings.layout === '2x2' ? '2×2' : '3×2';
    const paperLabel  = payslipSettings.paper === 'short' ? 'Short Bond'
      : payslipSettings.paper === 'long' ? 'Long Bond' : 'A4';
    const formatLabel = payslipSettings.format === 'colored' ? 'Colored' : 'Ink-Friendly';
    return `${layoutLabel} · ${paperLabel} · ${formatLabel}`;
  }, [payslipSettings]);

  // ── Handlers ─────────────────────────────────────────────────

  const handleRowClick = (emp) => {
    setSelectedEmployee(emp);
    setModalOpen(true);
  };

  const inkFriendly = payslipSettings.format === 'bw';

  const handleDownload = async (e, emp) => {
    e.stopPropagation();
    // Same-row guard only. Concurrent downloads are harmless: import() is deduped
    // by webpack and both generators are synchronous, building separate documents.
    const empKey = emp.id || emp.employeeId;
    if (downloadingId === empKey) return;
    setDownloadingId(empKey);
    let generatePayslipPdf;
    try {
      generatePayslipPdf = await loadPayslipPdf();
    } catch (err) {
      console.error('[PayslipList] PDF load error:', err);
      toast.error(PDF_LOAD_ERROR);
      setDownloadingId(null);
      return;
    }
    try {
      generatePayslipPdf(emp, run, company, inkFriendly);
    } catch (err) {
      console.error('[PayslipList] PDF error:', err);
      toast.error('Failed to generate the payslip PDF. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadAll = async () => {
    if (!run?.employees?.length || bulkGenerating || bulkLoading) return;
    // Fetch the chunk BEFORE raising the overlay — the overlay is full-screen and
    // non-dismissible, and a stalled chunk request would otherwise lock the page
    // for webpack's full 120s load timeout with no way out.
    setBulkLoading(true);
    let generateBulkPayslipPdf;
    try {
      generateBulkPayslipPdf = await loadBulkPayslipPdf();
    } catch (err) {
      console.error('[PayslipList] Bulk PDF load error:', err);
      toast.error(PDF_LOAD_ERROR);
      setBulkLoading(false);
      return;
    }
    setBulkLoading(false);
    setBulkGenerating(true);
    try {
      // Defer PDF generation one tick so the overlay renders first
      await new Promise((resolve) => setTimeout(resolve, 50));
      generateBulkPayslipPdf(run.employees, run, company, payslipSettings);
    } catch (err) {
      console.error('[PayslipList] Bulk PDF error:', err);
      toast.error('Failed to generate bulk PDF. Please try again.');
    } finally {
      setBulkGenerating(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────

  if (companyLoading || loading) {
    return (
      <div className="psl-loading">
        <div className="psl-loading__spinner" />
        <p className="psl-loading__text">Loading payslips…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="psl-error-state">
        <div className="psl-error-state__icon"><Icon name="alert-triangle" size={32} /></div>
        <div className="psl-error-state__message">{error}</div>
        <button
          className="psl-error-state__back"
          onClick={() => navigate(`/company/${slug}/payroll`)}
        >
          Back to Payroll Runs
        </button>
      </div>
    );
  }

  const displayRunId = run.runId || run.id;
  const period = formatPeriod(run.periodStart, run.periodEnd);

  return (
    <div className="psl-page">

      {/* Breadcrumb */}
      <nav className="psl-breadcrumb">
        <button
          className="psl-breadcrumb__link"
          onClick={() => navigate(`/company/${slug}/payroll`)}
        >
          Payroll Runs
        </button>
        <span className="psl-breadcrumb__sep">›</span>
        <span className="psl-breadcrumb__current">Run {displayRunId}</span>
      </nav>

      {/* Page Header */}
      <div className="psl-header">
        <div>
          <h1 className="psl-title">Payslips</h1>
          <p className="psl-subtitle">
            {period} · Run {displayRunId}
          </p>
        </div>
        <div className="psl-dl-wrap">
          <button
            className="psl-btn-download-all"
            onClick={handleDownloadAll}
            disabled={bulkGenerating || bulkLoading}
          >
            {bulkLoading ? 'Preparing…' : bulkGenerating ? 'Generating…' : '↓ Download All'}
          </button>
          <div className="psl-dl-hint">
            {settingsHint} ·{' '}
            <button
              className="psl-dl-hint__link"
              onClick={() => navigate(`/company/${slug}/settings/payslips`)}
            >
              Change in Settings →
            </button>
          </div>
        </div>
      </div>

      {/* Controls Bar */}
      <div className="psl-controls">
        <input
          type="text"
          className="psl-search"
          placeholder="Search employee…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <span className="psl-count">
          {filteredEmployees.length}{' '}
          {filteredEmployees.length === 1 ? 'payslip' : 'payslips'}
        </span>
      </div>

      {/* Table */}
      <div className="psl-table-wrapper">
        <table className="psl-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Department</th>
              <th>Salary Type</th>
              <th className="psl-th-right">Gross Pay</th>
              <th className="psl-th-right">Gov't Contributions</th>
              <th className="psl-th-right">Net Pay</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredEmployees.length === 0 ? (
              <tr>
                <td colSpan="7" className="psl-empty-cell">
                  {searchQuery
                    ? 'No employees match your search.'
                    : 'No employees in this payroll run.'}
                </td>
              </tr>
            ) : (
              filteredEmployees.map((emp) => {
                const name = getEmployeeName(emp);
                const fin = getEmployeeFinancials(emp);
                const empKey = emp.id || emp.employeeId;
                return (
                  <tr
                    key={empKey}
                    className="psl-row"
                    onClick={() => handleRowClick(emp)}
                  >
                    <td>
                      <div className="psl-employee-cell">
                        <div className="psl-avatar">{getInitials(name)}</div>
                        <div>
                          <div className="psl-employee-name">{name}</div>
                          <div className="psl-employee-id">#{emp.employeeId || '—'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="psl-td-secondary">{emp.employment?.department || '—'}</td>
                    <td className="psl-td-secondary">{getSalaryTypeLabel(emp)}</td>
                    <td className="psl-td-right">
                      <span className="psl-currency psl-currency--gross">
                        {formatCurrency(fin.gross)}
                      </span>
                    </td>
                    <td className="psl-td-right">
                      <span className="psl-currency psl-currency--deduction">
                        –{formatCurrency(fin.deductions)}
                      </span>
                    </td>
                    <td className="psl-td-right">
                      <span className="psl-currency psl-currency--net">
                        {formatCurrency(fin.net)}
                      </span>
                    </td>
                    <td>
                      <button
                        className="psl-btn-download"
                        onClick={(e) => handleDownload(e, emp)}
                        disabled={downloadingId === empKey}
                      >
                        {downloadingId === empKey ? 'Preparing…' : '↓ Download'}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Table Footer */}
      <div className="psl-footer">
        <div className="psl-footer-stats">
          <div className="psl-footer-stat">
            <span className="psl-footer-stat__label">Employees</span>
            <span className="psl-footer-stat__value">{run.employees?.length || 0}</span>
          </div>
          <div className="psl-footer-stat">
            <span className="psl-footer-stat__label">Total Gross</span>
            <span className="psl-footer-stat__value psl-footer-stat__value--gross">
              {formatCurrency(summary.totalGross)}
            </span>
          </div>
          <div className="psl-footer-stat">
            <span className="psl-footer-stat__label">Total Net Pay</span>
            <span className="psl-footer-stat__value psl-footer-stat__value--net">
              {formatCurrency(summary.totalNet)}
            </span>
          </div>
        </div>
        <div className="psl-footer-hint">
          Click row to preview · ↓ Download for individual PDF · ↓ Download All for compact bulk PDF per saved settings
        </div>
      </div>

      {bulkGenerating && (
        <div className="psl-bulk-overlay">
          <div className="psl-bulk-overlay__card">
            <div className="psl-bulk-overlay__spinner" />
            <div className="psl-bulk-overlay__label">Generating payslips PDF…</div>
            {run.employees?.length > 30 && (
              <div className="psl-bulk-overlay__sub">
                {run.employees.length} employees — this may take a moment
              </div>
            )}
          </div>
        </div>
      )}

      {modalOpen && selectedEmployee && (
        <PayslipModal
          employee={selectedEmployee}
          run={run}
          company={company}
          inkFriendly={inkFriendly}
          onClose={() => { setModalOpen(false); setSelectedEmployee(null); }}
        />
      )}

    </div>
  );
};

export default PayslipList;
